import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';

export interface ParamField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'boolean' | 'select' | 'date' | 'file';
  required?: boolean;
  options?: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  automationId: string;
  automationName: string;
  fields: ParamField[];
}

function apiErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: string; code?: string } } } | null)
    ?.response?.data;
  return data?.message ?? 'Déclenchement impossible. Réessayez dans un instant.';
}

export function RunAutomationModal({ open, onClose, automationId, automationName, fields }: Props) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  // Réinitialise les champs à chaque ouverture.
  useEffect(() => {
    if (open) {
      setValues({});
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/automations/${automationId}/trigger`, { params: values });
      return res.data as { executionId: string };
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['executions'] });
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  function setField(key: string, value: unknown) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    // Validation côté client des champs requis (l'API revalide de toute façon).
    const missing = fields.filter(
      (f) => f.required && (values[f.key] === undefined || values[f.key] === ''),
    );
    if (missing.length > 0) {
      setError(`Champs requis manquants : ${missing.map((f) => f.label).join(', ')}`);
      return;
    }
    mutation.mutate();
  }

  const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Exécuter — ${automationName}`}
      footer={
        mutation.isSuccess ? (
          <Button onClick={onClose} type="button">
            Fermer
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} type="button">
              Annuler
            </Button>
            <Button
              form="run-automation-form"
              type="submit"
              variant="accent"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Déclenchement…' : 'Exécuter maintenant'}
            </Button>
          </>
        )
      }
    >
      {mutation.isSuccess ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
          Exécution lancée ✓ — elle apparaît dans le journal des exécutions (statut « En cours »
          jusqu'au retour de n8n).
        </div>
      ) : (
        <form id="run-automation-form" onSubmit={handleSubmit} className="space-y-3" noValidate>
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {error}
            </div>
          )}

          {fields.length === 0 && (
            <p className="text-sm text-slate-500">
              Cette automatisation n'attend aucun paramètre. Cliquez sur « Exécuter maintenant ».
            </p>
          )}

          {fields.map((f) => {
            const id = `run-${f.key}`;
            const label = (
              <label htmlFor={id} className="mb-1 block text-xs font-medium text-slate-700">
                {f.label}
                {f.required && <span className="text-red-500"> *</span>}
              </label>
            );
            if (f.type === 'boolean') {
              return (
                <label key={f.key} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(values[f.key])}
                    onChange={(e) => setField(f.key, e.target.checked)}
                  />
                  {f.label}
                </label>
              );
            }
            if (f.type === 'select') {
              return (
                <div key={f.key}>
                  {label}
                  <select
                    id={id}
                    value={String(values[f.key] ?? '')}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— choisir —</option>
                    {(f.options ?? []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
            if (f.type === 'textarea') {
              return (
                <div key={f.key}>
                  {label}
                  <textarea
                    id={id}
                    rows={3}
                    value={String(values[f.key] ?? '')}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className={inputCls}
                  />
                </div>
              );
            }
            const inputType = f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text';
            return (
              <div key={f.key}>
                {label}
                <input
                  id={id}
                  type={inputType}
                  value={String(values[f.key] ?? '')}
                  onChange={(e) =>
                    setField(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)
                  }
                  className={inputCls}
                />
                {f.type === 'file' && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Saisissez une URL/référence de fichier (l'envoi de fichier passe par le
                    formulaire n8n).
                  </p>
                )}
              </div>
            );
          })}
        </form>
      )}
    </Modal>
  );
}
